import type { ProductOption } from "@/types/shopify"
import type { CogsSource } from "@/lib/shopify/economics/effective-cogs"
import type { CogsEditPlan } from "@/lib/shopify/economics/cogs-edit-scope"

export type EconomicsTab = "config" | "bundle" | "matrix"

/** An item in the economics analysis list — either a saved set or an individual product */
export type AnalysisItem =
  | { type: "set"; id: string; name: string; products: ProductOption[] }
  | { type: "product"; product: ProductOption }

/** Derive deduplicated product IDs from analysis items */
export function deriveProductIds(items: AnalysisItem[]): number[] {
  const ids = new Set<number>()
  for (const item of items) {
    if (item.type === "set") item.products.forEach((p) => ids.add(p.product_id))
    else ids.add(item.product.product_id)
  }
  return [...ids]
}

/**
 * Cheap, stable fingerprint of analysis items for dirty detection against a
 * preset snapshot. Captures item identity and set membership — sufficient to
 * know when the user's current selection diverges from what was loaded.
 * Avoids JSON.stringify on the full tree (full ProductOption payloads).
 */
export function fingerprintAnalysisItems(items: AnalysisItem[]): string {
  const parts: string[] = []
  for (const item of items) {
    if (item.type === "product") {
      parts.push(`p:${item.product.product_id}`)
    } else {
      const memberIds = item.products.map((p) => p.product_id).sort((a, b) => a - b).join(",")
      parts.push(`s:${item.id}:${memberIds}`)
    }
  }
  parts.sort()
  return parts.join("|")
}

/** A saved view = snapshot of analysis configuration (Config or Bundle) */
export interface SavedEconomicsPreset {
  id: string
  integration_id: string
  name: string
  items: AnalysisItem[]
  // Bundle-specific (optional)
  baseline_mode?: string
  custom_baseline_start?: string | null
  custom_baseline_end?: string | null
  created_at: string
  updated_at: string
}

/** A Shopify variant, addressed by its legacy product and variant ids. */
export interface VariantRef {
  product_id: number
  variant_id: number
}

/** Hierarchy level behind a row's COGS — `mixed` when its line items resolved differently. */
export type EconomicsRowCogsSource = CogsSource | "mixed"

/** Raw data from /api/integrations/shopify/product-economics */
export interface ConfigEconomicsItem {
  /**
   * Representative (first-seen) product_id. A row is keyed by SKU, so the same
   * physical SKU under multiple Shopify listings collapses into one row — see
   * `product_ids` for the full set. Used as the set-membership anchor.
   */
  product_id: number
  /**
   * Every distinct product_id that contributed to this SKU row (duplicate
   * listings). Single-element for a normally-listed SKU. Drives custom-mode
   * filtering (match ANY).
   */
  product_ids: number[]
  title: string
  sku: string | null
  variant_title: string | null

  order_count: number
  order_ids: string[]
  units_sold: number
  total_revenue: number
  /**
   * Sum of discount allocations on this product's line_items across all
   * matching orders. Already netted from `total_revenue` (Shopify ships
   * line_total = unit_price × qty − total_discount), so this is purely
   * informational — surfaces as a Discounts column. Does NOT factor into
   * Net Profit recompute.
   */
  total_discount: number
  avg_unit_price: number
  /** Resolved COGS (manual override or Shopify cost) of the `units_with_cogs` units. */
  total_cogs: number | null
  /** Units whose line resolved a COGS; the rest fall back to the global default on the client. */
  units_with_cogs: number
  cogs_source: EconomicsRowCogsSource
  /** Every (product, variant) pair sold in this row. */
  variant_refs: VariantRef[]
  /** What an inline "this SKU only" COGS edit writes and clears; null when the row is read-only (and in the Family view). */
  cogs_edit: CogsEditPlan | null

  units_returned: number
  refund_amount: number

  allocated_shipping: number
  allocated_shopify_fee: number
  /** Number of orders where order subtotal >= free shipping threshold */
  free_shipping_order_count: number
}

export interface ConfigEconomicsTotals {
  total_orders: number
  total_units_sold: number
  total_revenue: number
  /** See ConfigEconomicsItem.total_discount — informational, already netted from total_revenue. */
  total_discount: number
  total_cogs: number | null
  avg_revenue_per_order: number
  total_units_returned: number
  total_refund_amount: number
  total_allocated_shipping: number
  total_allocated_shopify_fee: number
}

export interface ProductEconomicsResponse {
  products: ConfigEconomicsItem[]
  totals: ConfigEconomicsTotals
  /** Present when route was called with group_by=family. */
  families?: FamilyEconomicsItem[]
  /** Echoes the request's group_by ("product" | "family"). Default "product". */
  group_by?: "product" | "family"
}

/**
 * Per-family aggregate row (group_by="family"). Reuses the same numeric metrics
 * as ConfigEconomicsItem so client-side computeEconomicsRow / row-cache can run
 * on the same shape — only the identifier differs (family_key + label, no
 * product_id / sku / variant_title).
 *
 * `skus` carries the SKU breakdown for drill-down (accordion under the family
 * row). Each entry is a regular ConfigEconomicsItem — same shape served by
 * group_by="product" — so the existing per-SKU rendering can be reused as-is.
 */
export interface FamilyEconomicsItem {
  family_key: string
  /** Resolved via productFamilyRulesDal.list (rule.name) with title fallback. */
  label: string
  /** Rule id when family_key matches an enabled rule; null for system fallback keys. */
  rule_id: string | null
  /** Distinct order count and metric block — same shape as ConfigEconomicsItem. */
  order_count: number
  order_ids: string[]
  units_sold: number
  total_revenue: number
  /** See ConfigEconomicsItem.total_discount — informational, already netted from total_revenue. */
  total_discount: number
  avg_unit_price: number
  total_cogs: number | null
  /** See ConfigEconomicsItem.units_with_cogs. */
  units_with_cogs: number
  units_returned: number
  refund_amount: number
  allocated_shipping: number
  allocated_shopify_fee: number
  free_shipping_order_count: number
  /** Per-SKU breakdown — drill-down rows under the family. */
  skus: ConfigEconomicsItem[]
}

/** Global params persisted in tenant_settings */
export interface EconomicsSettings {
  blended_cac: number
  shipping_cost: number
  return_label_cost: number
  shopify_fee_pct: number
  free_shipping_threshold: number
  default_cogs_per_unit: number
}

export const DEFAULT_ECONOMICS_SETTINGS: EconomicsSettings = {
  blended_cac: 0,
  shipping_cost: 0,
  return_label_cost: 0,
  shopify_fee_pct: 0.0225,
  free_shipping_threshold: 99.99,
  default_cogs_per_unit: 0,
}

export type ShippingPaidBy = "Brand" | "Customer" | "Mixed"

/** Computed economics fields (client-side from raw + params) */
export interface ComputedEconomics {
  distinct_orders: number
  effective_cogs: number | null
  cogs_available: boolean
  /** False while some units have no cost at any level (no default to fall back on). */
  cogs_complete: boolean
  return_rate: number
  refund_cost: number
  return_labels_cost: number
  shipping_paid_by: ShippingPaidBy
  brand_shipping: number
  total_cac: number
  shopify_fee: number
  net_profit: number
  profit_per_order: number
  margin_pct: number
}

